// Cliente de /api/oficina/despliegues (bandeja «Despliegues pendientes de mi Allow», sprint 127 F4).
// Espejo de backend/src/despliegues/servicio.ts (DespliegueDTO).
import { api } from '../../../lib/api';

export type CambiosEnv = { tipo: 'ninguno' } | { tipo: 'valor'; texto: string } | { tipo: 'desconocido' };

/** Lo que lee Pau (espejo de ParaPau en el backend). */
export interface ParaPau {
  titulo: string;
  cambia: string | null;
  cambiaEstado: 'listo' | 'preparando' | 'no_disponible' | 'no_aplica';
  apruebas: string;
  siSaleMal: string;
  /** 05-10: qué pasa si lo rechazas (qué sale de la versión oficial); null = nada especial. */
  siRechazas: string | null;
}

/** 05-10: un cambio de los que sube (lo calcula Calypso antes de la tarjeta). */
export interface ItemSube {
  pr: number | null;
  titulo: string;
  agente: string | null;
  tema: string | null;
  /** Lo que el agente te escribió al integrarlo (2-3 frases), o null. */
  resumen: string | null;
  afectaFans: boolean;
  url: string | null;
  /** Saca algo que rechazaste: rechazar la subida no la deshace. */
  esRetirada?: boolean;
  /** Vuelve a meter algo que rechazaste (número de aquel cambio). */
  readmite?: number | null;
}

/** Todo lo que sube (una subida lleva la versión oficial entera desde lo que hay en producción). */
export type LoQueSube =
  | { estado: 'ok'; total: number; items: ItemSube[] }
  | { estado: 'mismo_codigo' | 'exento' | 'sin_lectura' | 'desconocido' };

/** El agente que pidió el despliegue (se sabe por su conversación, no por GitHub). null = no se sabe. */
export interface PedidoPor {
  agentId: string;
  /** Lo que hace ese agente, en palabras de Pau. */
  titulo: string;
  /** Su nombre en la oficina («Río»), o null si no es de la oficina. */
  nombre: string | null;
  /** Sigue en el panel y se le puede escribir. */
  vivo: boolean;
  /** false = había más de un candidato: «creo que lo pidió». */
  seguro: boolean;
}

/** «Más información»: la explicación larga, en palabras de Pau. */
export type EstadoMasInfo = { estado: 'listo'; info: MasInfo } | { estado: 'preparando' } | { estado: 'no_disponible' };

export interface MasInfo {
  puntos: string[];
  porQue: string;
  comprobado: string;
  riesgos: string;
  partes: string[];
}

/** Un mensaje de la conversación con quien pidió el despliegue. */
export interface MensajeHilo {
  id: string;
  de: 'pau' | 'agente';
  texto: string;
  ts: string;
}

/** 13-10: una línea por cosa que cambia en producción (espejo de LineaTarjeta en backend/src/despliegues/lineas.ts). */
export interface Linea {
  /** «l1», «l2»…: para «Preguntar» y «Quitar». */
  id: string;
  titulo: string;
  antes: string | null;
  ahora: string;
  a_quien: { tipo: 'todos' | 'parte' | 'nadie' | 'desconocido'; texto: string };
  /** Texto nuevo que leerá Calypso, traducido. */
  frases_nuevas: string[];
  /** Si Pau lo pidió en el chat con ese agente (comprobado con su frase literal). */
  hablado: 'si' | 'no' | 'rechazado' | 'desconocido';
  hablado_cita: string | null;
  clase: 'codigo' | 'ajuste';
  agente: string | null;
  agentId: string | null;
  confianza: 'alta' | 'baja';
  fuentes: string[];
  /** Qué sale si la quitas (trabajos enteros y ajustes, y las líneas que arrastra); null = no se quita suelta. */
  quitar: {
    prs: number[];
    trabajos: Array<{ pr: number; titulo: string; agente: string | null }>;
    ajustes: string[];
    arrastra: string[];
  } | null;
}

export type EstadoLineas =
  | { estado: 'listo'; lineas: Linea[]; sinEfecto: Array<{ agente: string | null; titulo: string }> }
  | { estado: 'preparando' }
  | { estado: 'no_disponible'; motivo: string };

export interface Despliegue {
  runId: number;
  paraPau: ParaPau;
  /** Quién lo pidió (se sabe por su conversación, no por GitHub), o null. */
  pedidoPor: PedidoPor | null;
  /** Si ese agente ya tuvo un despliegue devuelto con nota tuya: la nota. */
  vuelveTrasNota: string | null;
  /** 05-10: si ya aprobaste hoy este mismo trabajo, cuándo y por qué vuelve a pedírtelo. */
  antecedente: string | null;
  /** 13-10: una línea por cosa que cambia (se prepara en segundo plano, 1-3 min). */
  lineas: EstadoLineas;
  /** Lo que se pidió desplegar (rama o SHA), para los detalles técnicos. */
  gitRef: string | null;
  intento: number;
  workflow: string;
  archivo: string;
  soloLectura: boolean;
  esDeploy: boolean;
  rama: string;
  /** Lo que se ejecuta/despliega de verdad; null = no se ha podido saber (va en `avisos`). */
  sha: string | null;
  titulo: string;
  pr: { numero: number; url: string } | null;
  vueltaAtras: string | null;
  cambiosEnv: CambiosEnv;
  copias: string | null;
  refrescaNginx: boolean | null;
  loQueSube: LoQueSube;
  /** Lo que hay que saber antes de decidir, en rojo. */
  avisos: string[];
  lanzadoPor: string;
  creadoEn: string;
  comoSeDeshace: string;
  url: string;
}

export interface Bandeja {
  pendientes: Despliegue[];
  /** Algún run no se pudo leer: puede haber más pendientes de los que se ven. */
  incompleta: boolean;
  contrasenaFijada: boolean;
  /** Cuentas personales: se confirma con el código de Google Authenticator de quien decide, no con la contraseña. */
  conCodigo?: boolean;
  bloqueadoHasta: string | null;
  historial: Array<{ en: string; quien: string; runId: number; workflow: string; sha: string; decision: string; resultado: string }>;
}

const BASE = '/api/oficina/despliegues';

export const desplieguesApi = {
  bandeja: (fresco = false) => api<Bandeja>(`${BASE}${fresco ? '?fresco=1' : ''}`),
  fijarContrasena: (contrasenaPanel: string, nueva: string) => api<{ ok: true }>(`${BASE}/contrasena`, { method: 'POST', body: { contrasenaPanel, nueva } }),
  /** `visto`: lo que Pau tenía delante; si el run ha cambiado desde entonces, el servidor no decide. */
  /** `quitar` (13-10): ids de las líneas que salen con este Allow; solo al aprobar. */
  decidir: (d: Pick<Despliegue, 'runId' | 'intento' | 'sha'>, decision: 'aprobar' | 'rechazar', contrasena: string, nota?: string, quitar: string[] = []) =>
    api<{ resultado: 'hecho' }>(`${BASE}/${d.runId}/decidir`, {
      method: 'POST',
      body: { decision, contrasena, visto: { intento: d.intento, sha: d.sha }, ...(nota ? { nota } : {}), ...(decision === 'aprobar' && quitar.length ? { quitar } : {}) },
    }),
  masInfo: (runId: number) => api<EstadoMasInfo>(`${BASE}/${runId}/mas-info`),
  /** `linea` (13-10, «Preguntar»): la conversación con el agente que hizo esa línea. */
  hilo: (runId: number, linea?: string) => api<{ mensajes: MensajeHilo[] }>(`${BASE}/${runId}/hilo${linea ? `?linea=${linea}` : ''}`),
  escribir: (runId: number, texto: string, linea?: string) => api<{ mensajes: MensajeHilo[] }>(`${BASE}/${runId}/hilo`, { method: 'POST', body: { texto, ...(linea ? { linea } : {}) } }),
};
